import React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CheckCircle2, ChevronDown, ChevronUp, ArrowDown, ArrowUp, SendHorizontal, Loader2, Clock } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { ChargeDetectionFeedbackRequest, FeedbackRating } from "@/chargeDetection/types"

interface FeedbackFormProps {
  installationId: number
  analyzedOnPeriodStart: string
  analyzedOnPeriodStop: string
  predictedCharge: number
  extractedFeatures?: Array<Record<string, unknown>>
  onSubmit: (feedback: ChargeDetectionFeedbackRequest) => Promise<void>
  isSubmitting: boolean
  isSuccess: boolean
}

export function FeedbackForm({
  installationId,
  analyzedOnPeriodStart,
  analyzedOnPeriodStop,
  predictedCharge,
  extractedFeatures,
  onSubmit,
  isSubmitting,
  isSuccess,
}: FeedbackFormProps) {
  const [rating, setRating] = React.useState<FeedbackRating | null>(null)
  const [lastChargeDate, setLastChargeDate] = React.useState("")
  const [lastChargeTime, setLastChargeTime] = React.useState("")
  const [lastChargeAmount, setLastChargeAmount] = React.useState("")
  const [showOptionalFields, setShowOptionalFields] = React.useState(false)

  const handleSetToNow = () => {
    const now = new Date()
    const localDate = now.toISOString().slice(0, 10) // YYYY-MM-DD
    const localTime = now.toTimeString().slice(0, 5) // HH:MM
    setLastChargeDate(localDate)
    setLastChargeTime(localTime)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!rating) {
      return
    }

    const feedback: ChargeDetectionFeedbackRequest = {
      installation: installationId,
      analyzed_on_period_start: analyzedOnPeriodStart,
      analyzed_on_period_stop: analyzedOnPeriodStop,
      predicted_charge: predictedCharge,
      rating,
      ...(extractedFeatures && { extracted_features: extractedFeatures }),
      ...(lastChargeDate && { last_charge_date: lastChargeDate }),
      ...(lastChargeTime && { last_charge_time: lastChargeTime }),
      ...(lastChargeAmount && { last_charge_amount: lastChargeAmount }),
    }

    await onSubmit(feedback)
  }

  if (isSuccess) {
    return (
      <Alert className="border-green-500 bg-green-50">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          Thank you for your feedback! It has been submitted successfully.
        </AlertDescription>
      </Alert>
    )
  }

  const ratingOptions: Array<{ value: FeedbackRating; label: string; icon: React.ReactNode; color: string }> = [
    {
      value: 'underestimated',
      label: 'Too low',
      icon: <ArrowDown className="h-5 w-5" />,
      color: 'border-orange-500 bg-orange-50 hover:bg-orange-100 text-orange-700'
    },
    {
      value: 'accurate',
      label: 'Accurate',
      icon: <CheckCircle2 className="h-5 w-5" />,
      color: 'border-green-500 bg-green-50 hover:bg-green-100 text-green-700'
    },
    {
      value: 'overestimated',
      label: 'Too high',
      icon: <ArrowUp className="h-5 w-5" />,
      color: 'border-red-500 bg-red-50 hover:bg-red-100 text-red-700'
    },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-sm font-normal mb-3 block">
          How accurate is this prediction?
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {ratingOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setRating(option.value)}
              className={`
                p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2
                ${rating === option.value
                  ? option.color
                  : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }
              `}
            >
              {option.icon}
              <span className="text-sm font-medium">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <Collapsible open={showOptionalFields} onOpenChange={setShowOptionalFields}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-between"
          >
            <span className="text-sm text-gray-600">
              Add actual charge details (optional)
            </span>
            {showOptionalFields ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                When did you last charge it?
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline_secondary"
                onClick={handleSetToNow}
              >
                <Clock className="h-4 w-4 mr-1" />
                Just now
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="lastChargeDate" className="text-xs text-gray-600">
                  Date
                </Label>
                <Input
                  id="lastChargeDate"
                  type="date"
                  value={lastChargeDate}
                  onChange={(e) => setLastChargeDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lastChargeTime" className="text-xs text-gray-600">
                  Time (optional)
                </Label>
                <Input
                  id="lastChargeTime"
                  type="time"
                  value={lastChargeTime}
                  onChange={(e) => setLastChargeTime(e.target.value)}
                  className="mt-1"
                  disabled={!lastChargeDate}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastChargeAmount" className="text-sm font-medium">
              How much did you charge it by?
            </Label>
            <Input
              id="lastChargeAmount"
              type="text"
              placeholder="e.g. 10 oz, 1 lb, 2 kg"
              value={lastChargeAmount}
              onChange={(e) => setLastChargeAmount(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Enter the actual charge amount in any format
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Button
        type="submit"
        variant="thalo"
        disabled={!rating || isSubmitting}
        className="w-full"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <SendHorizontal className="h-4 w-4 mr-2" />
            Submit Feedback
          </>
        )}
      </Button>
    </form>
  )
}
